import { graphql } from 'react-relay';
import type { FC } from 'react';
import React, { useCallback, useRef, useState, memo } from 'react';
import { useIntl, defineMessages, FormattedMessage } from 'react-intl';
import type { MutationUpdaterFn } from 'apollo-client';
import { css } from '@compiled/react';
import ChevronRightIcon from '@atlaskit/icon/core/chevron-right';
import ChevronLeftIcon from '@atlaskit/icon/core/chevron-left';
import { token } from '@atlaskit/tokens';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import Button from '@atlaskit/button/custom-theme-button';
import ShowMoreHorizontalIcon from '@atlaskit/icon/core/show-more-horizontal';
import DropdownMenu from '@atlaskit/dropdown-menu/dropdown-menu';
import DropdownItem from '@atlaskit/dropdown-menu/dropdown-menu-item';
import { useFlags } from '@atlaskit/flag/flag-provider';
import StatusErrorIcon from '@atlaskit/icon/core/status-error';
import Heading from '@atlaskit/heading/heading';

import type { ApolloErrorInterop } from '@confluence/apollo-relay-interop-utils/entry-points/ApolloErrorInterop.ts';
import { useMutationInterop } from '@confluence/apollo-relay-interop/entry-points/useMutationInterop.ts';
import { readQueryInterop } from '@confluence/apollo-relay-interop/entry-points/readQuery.ts';
import { writeQueryInterop } from '@confluence/apollo-relay-interop/entry-points/writeQuery.ts';
import { ErrorDisplay } from '@confluence/error-boundary/entry-points/ErrorDisplay.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { TransparentErrorBoundary } from '@confluence/error-boundary/entry-points/TransparentErrorBoundary.ts';
import { useElementSeenCallback } from '@confluence/analytics/entry-points/useElementSeenCallback.ts';

import { isFeedRecDeletedSpaceError } from '../expectedGraphqlErrorHelpers.ts';
import { RecommendationTile, TILE_MARGIN, PAGE_SIZE } from './RecommendationTile.tsx';
import type { RecommendationType } from './useFeedRecommendations.ts';
import { FeedRecommendationQuery } from './FeedRecommendationQuery.graphql.ts';
import type {
	FeedRecommendationsFeedRecDismissMutation as FeedRecommendationsFeedRecDismissMutation$data,
	FeedRecommendationsFeedRecDismissMutationVariables as FeedRecommendationsFeedRecDismissMutation$variables,
} from './__types__/FeedRecommendationsFeedRecDismissMutation.ts';
import type { FeedRecommendationQuery as FeedRecommendationQueryType } from './__types__/FeedRecommendationQuery.ts';
import type { FeedRecDismissMutation as FeedRecDismissMutationType } from './__types__/FeedRecDismissMutation.ts';

const FeedRecommendationsFeedRecDismissMutation = graphql`
	mutation FeedRecommendationsFeedRecDismissMutation($dismissTime: String!) {
		updateUserPreferences(
			userPreferences: { feedRecommendedUserSettingsDismissTimestamp: $dismissTime }
		) {
			feedRecommendedUserSettingsDismissTimestamp
		}
	}
`;
const i18n = defineMessages({
	title: {
		id: 'feed.recommendation.title',
		defaultMessage: 'Improve your feed',
		description:
			'The text is shown as the heading of the feed recommendations card, which appears in the feed to encourage users to follow more spaces or people so their feed content improves.',
	},
	dismiss: {
		id: 'feed.recommendation.dismiss',
		defaultMessage: 'Hide suggestions',
		description: 'Text to display in the dropdown option to dismiss recommendations',
	},
	dismissDescription: {
		id: 'feed.recommendation.dismiss.description',
		defaultMessage: 'Don’t show this card for one month',
		description: 'Description text for the hide suggestions option to dismiss recommendations',
	},
	flagErrorTitle: {
		id: 'feed.recommendation.flag.dismiss.error',
		defaultMessage: 'Could not dismiss recommendations',
		description: 'Text to display for the title of the dismissal error flag',
	},
	flagErrorDescription: {
		id: 'feed.recommendation.flag.dismiss.error.description',
		defaultMessage: 'Please try again later',
		description: 'Text for the dismiss error flag description',
	},
	moreIconLabel: {
		id: 'feed.recommendations.more.label',
		defaultMessage: 'Show more options',
		description: 'Label text for the more icon dropdown menu trigger',
	},
	leftCarouselLabel: {
		id: 'feed.recommendations.carousel.left',
		defaultMessage: 'Scroll left',
		description:
			'The text is used as the accessible aria-label for the left navigation button in the recommendations carousel within the feed, allowing users to scroll the carousel to the left to see previous recommendation tiles.',
	},
	rightCarouselLabel: {
		id: 'feed.recommendations.carousel.right',
		defaultMessage: 'Scroll right',
		description: 'Label text for the carousel right button',
	},
});

const menuButtonTheme = (currentTheme, themeProps) => {
	const { buttonStyles, spinnerStyles } = currentTheme(themeProps);
	return {
		buttonStyles: {
			...buttonStyles,
			backgroundColor: token('color.background.input'),
			'&:hover': {
				backgroundColor: token('color.background.input.hovered'),
			},
			"&[aria-expanded='true']": {
				backgroundColor: token('color.background.accent.gray.bolder'),
				color: token('color.text.inverse'),
			},
		},
		spinnerStyles,
	};
};

const titleAndMenuWrapperStyle = css({
	display: 'flex',
	justifyContent: 'space-between',
	alignItems: 'center',
	marginBottom: token('space.100'),
});

const menuWrapperStyle = css({
	display: 'flex',
	flexDirection: 'row',
});

const tilesWrapperStyle = css({
	display: 'flex',
	justifyContent: 'flex-start',
	margin: `0px ${token('space.negative.200')}`,
	padding: `0px ${token('space.200')}`,

	overflowX: 'auto',
	scrollSnapType: 'x mandatory',
	scrollPaddingLeft: '16px',
	'-ms-overflow-style': 'none',
	scrollbarWidth: 'none',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors
	'&::-webkit-scrollbar': {
		display: 'none',
	},

	pointerEvents: 'none',
});

const isIgnorableError = (error: ApolloErrorInterop) => {
	return error.graphQLErrors.every(isFeedRecDeletedSpaceError);
};

const updateFeedRecDismiss: MutationUpdaterFn<FeedRecDismissMutationType> = (_cache, { data }) => {
	const timestamp = data?.updateUserPreferences?.feedRecommendedUserSettingsDismissTimestamp;
	const cachedata = readQueryInterop<FeedRecommendationQueryType>({
		query: FeedRecommendationQuery,
	});
	if (timestamp && cachedata) {
		const newData = { ...cachedata };
		newData.userPreferences.feedRecommendedUserSettingsDismissTimestamp = timestamp;
		writeQueryInterop({
			query: FeedRecommendationQuery,
			data: newData,
		});
	}
};

export type FeedRecommendationProps = {
	position: number;
	recs: RecommendationType[];
	setRecs: (arg0: RecommendationType[]) => void;
	feedRecError?: ApolloErrorInterop;
	setShowFeedRecs: (arg0: boolean) => void;
};

const FeedRecommendationsComponent: FC<FeedRecommendationProps> = ({
	position,
	recs,
	setRecs,
	feedRecError,
	setShowFeedRecs,
}) => {
	const intl = useIntl();

	const bottomOfCardRef = useRef<HTMLDivElement | null>(null);
	const tilesWrapperRef = useRef<HTMLDivElement | null>(null);
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [recOffset, setRecOffset] = useState(0);
	const { showFlag } = useFlags();

	const recsToShow = recs.slice(recOffset, recOffset + PAGE_SIZE);
	const isRightmostPage = recOffset === recs.length - PAGE_SIZE && recOffset !== 0;

	const onRecommendationsViewed = useCallback(() => {
		createAnalyticsEvent({
			type: 'sendTrackEvent',
			data: {
				action: 'rendered',
				actionSubject: 'suggestionsCard',
				source: 'homeScreen',
				attributes: {
					pos: position,
					numSuggestions: recs.length,
					followSuggestions: recsToShow.map((rec) => rec.id),
					followSuggestionTypes: recsToShow.map((rec) => rec.type),
				},
			},
		}).fire();
	}, [createAnalyticsEvent, position, recs, recsToShow]);
	useElementSeenCallback(bottomOfCardRef, onRecommendationsViewed);

	const onDismissError = useCallback(() => {
		showFlag({
			title: <FormattedMessage {...i18n.flagErrorTitle} />,
			description: <FormattedMessage {...i18n.flagErrorDescription} />,
			icon: <StatusErrorIcon label="error" color={token('color.icon.danger')} spacing="spacious" />,
			isAutoDismiss: true,
		});
	}, [showFlag]);

	const hideFeedRecs = () => {
		setShowFeedRecs(false);
	};
	const [dismissFeedRec, { error: dismissError }] = useMutationInterop<
		FeedRecommendationsFeedRecDismissMutation$data,
		FeedRecommendationsFeedRecDismissMutation$variables
	>(FeedRecommendationsFeedRecDismissMutation, {
		onCompleted: hideFeedRecs,
		update: updateFeedRecDismiss,
		onError: onDismissError,
	});
	const handleDismissClick = useCallback(() => {
		const date = new Date();
		void dismissFeedRec({
			variables: {
				dismissTime: date.toISOString(),
			},
		});
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'suggestionsDismissal',
				source: 'suggestionsCard',
				attributes: {
					timestamp: date.toISOString(),
					pos: position,
					numSuggestions: recs.length,
					followSuggestions: recsToShow.map((rec) => rec.id),
					followSuggestionTypes: recsToShow.map((rec) => rec.type),
				},
			},
		}).fire();
	}, [dismissFeedRec, createAnalyticsEvent, position, recs, recsToShow]);

	const scrollTilesWrapper = useCallback(
		(newRecOffset: number) => {
			if (tilesWrapperRef.current) {
				const scrollPosition =
					newRecOffset *
					(tilesWrapperRef.current.offsetWidth / PAGE_SIZE - TILE_MARGIN / PAGE_SIZE);
				tilesWrapperRef.current.scroll({
					left: scrollPosition,
					behavior: 'smooth',
				});
				setRecOffset(newRecOffset);
			}
		},
		[tilesWrapperRef, setRecOffset],
	);

	const dismissTile = useCallback(
		(id: string) => {
			const newRecs = recs.filter((rec) => rec.id !== id);
			const newShownRecs = isRightmostPage
				? newRecs.slice(recOffset - 1, recOffset - 1 + PAGE_SIZE)
				: newRecs.slice(recOffset, recOffset + PAGE_SIZE);
			if (isRightmostPage) {
				scrollTilesWrapper(recOffset - 1);
			}
			setRecs(newRecs);

			createAnalyticsEvent({
				type: 'sendTrackEvent',
				data: {
					action: 'rendered',
					actionSubject: 'suggestionsCard',
					source: 'homeScreen',
					attributes: {
						pos: position,
						numSuggestions: newRecs.length,
						followSuggestions: newShownRecs.map((rec) => rec.id),
						followSuggestionTypes: newShownRecs.map((rec) => rec.type),
					},
				},
			}).fire();
		},
		[recs, recOffset, isRightmostPage, setRecs, createAnalyticsEvent, position, scrollTilesWrapper],
	);

	const handleLeftCarousel = useCallback(() => {
		scrollTilesWrapper(Math.max(0, recOffset - 4));
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'leftCarousel',
				source: 'suggestionsCard',
				attributes: {
					pos: position,
				},
			},
		}).fire();
	}, [scrollTilesWrapper, recOffset, createAnalyticsEvent, position]);

	const handleRightCarousel = useCallback(() => {
		scrollTilesWrapper(Math.min(recs.length - PAGE_SIZE, recOffset + 4));
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'rightCarousel',
				source: 'suggestionsCard',
				attributes: {
					pos: position,
				},
			},
		}).fire();
	}, [recs, scrollTilesWrapper, recOffset, createAnalyticsEvent, position]);

	const getDropdownTriggerButton = useCallback(
		({ triggerRef, ...props }: any) => {
			return (
				<Button
					{...props}
					ref={triggerRef}
					iconBefore={
						<ShowMoreHorizontalIcon
							label={intl.formatMessage(i18n.moreIconLabel)}
							spacing="spacious"
						/>
					}
					theme={menuButtonTheme}
				/>
			);
		},
		[intl],
	);

	if (feedRecError && !isIgnorableError(feedRecError)) {
		return <ErrorDisplay error={feedRecError} />;
	}
	if (recs.length === 0) {
		return null;
	}

	return (
		<>
			{dismissError && <ErrorDisplay error={dismissError} />}
			<div css={titleAndMenuWrapperStyle}>
				<Heading as="h2" size="xsmall">
					<FormattedMessage {...i18n.title} />
				</Heading>
				<div css={menuWrapperStyle}>
					<Button
						theme={menuButtonTheme}
						onClick={handleLeftCarousel}
						iconBefore={
							<ChevronLeftIcon
								label={intl.formatMessage(i18n.leftCarouselLabel)}
								spacing="spacious"
								size="small"
							/>
						}
						isDisabled={recOffset === 0}
					/>
					<Button
						theme={menuButtonTheme}
						onClick={handleRightCarousel}
						iconBefore={
							<ChevronRightIcon
								label={intl.formatMessage(i18n.rightCarouselLabel)}
								spacing="spacious"
								size="small"
							/>
						}
						isDisabled={isRightmostPage || recs.length <= PAGE_SIZE}
					/>
					<DropdownMenu
						placement="bottom-end"
						trigger={getDropdownTriggerButton}
						testId="feed-rec-dropdown-menu"
					>
						<DropdownItem
							onClick={handleDismissClick}
							description={intl.formatMessage(i18n.dismissDescription)}
						>
							<FormattedMessage {...i18n.dismiss} />
						</DropdownItem>
					</DropdownMenu>
				</div>
			</div>
			<div css={tilesWrapperStyle} ref={tilesWrapperRef} data-testid="feed-rec-tiles-wrapper">
				{recs.map((rec, index) => (
					<RecommendationTile
						key={rec.id}
						name={rec.name}
						id={rec.id}
						url={rec.url}
						icon={rec.icon}
						type={rec.type}
						title={rec.title}
						dismissTile={dismissTile}
						cardIndex={index}
						position={position}
					/>
				))}
			</div>
			<div ref={bottomOfCardRef} />
		</>
	);
};

export const FeedRecommendations: FC<FeedRecommendationProps> = memo((props) => {
	return (
		<TransparentErrorBoundary attribution={Attribution.SMARTS}>
			<FeedRecommendationsComponent {...props} />
		</TransparentErrorBoundary>
	);
});
